<script setup>
/**
 * タイムチャート作成：要望ルールの確認（読み取り専用プレビュー）（DEV-14984）
 *
 * - ステップ2 に入ったとき、要望が未解析（rulesStale）なら AI で自動解析してプレビューを表示（親が parseRules を呼ぶ）
 *   未解析でなければ保存済みのルールをそのまま表示する
 * - ルールの追加・編集・削除はできない。要望を直す場合は「編集」でステップ1 に戻る
 * - 解析できなかった要望（resolve=AMBIGUOUS）は黄色で目立たせるが、そのまま確定できる（AI が原文を参考に判断する）
 * - 配給会社の要望なのに必須条件にならなかったもの（AMBIGUOUS・OTHER・サーバー検証で外れたもの）は
 *   警告タグと原文・理由を出して確認を促す（確定は止めない）
 * - 「ルールを確定」で PUT /rules → AI 生成を開始してステップ3 へ（処理は親が行う）
 */
import { computed } from "vue";
import { usePlanState } from "../use-plan";
import {
  RULE_KIND_OPTIONS,
  RULE_SOURCE_OPTIONS,
  isDistributorSoftRule,
  optionLabel,
  periodLabel,
  versionLabel,
} from "../utils";

const emit = defineEmits(["edit", "confirm"]);

const state = usePlanState();
const {
  canEdit, movies, rules, rulesStale, rulesDirty, rulesParsing, rulesSaving, parseError,
  movieMap, screenMap, generating, generateBlockReason,
} = state;

const ambiguousCount = computed(() => rules.value.filter((r) => r.resolve === "AMBIGUOUS").length);
const hasRequestText = computed(() => movies.value.some((m) => m.distributorText || m.cinemaRequestText));
// 表示中のルールが未確定のプレビューか（解析結果を表示中、またはバックエンドが未解析と判定）
const isPreview = computed(() => rulesStale.value || rulesDirty.value);

const movieTitle = (id) => (id == null ? "全作品" : movieMap.value.get(id)?.title || `作品ID ${id}`);
const ruleVersionLabel = (r) => {
  if (r.versionId == null) return "全バージョン";
  const movie = movieMap.value.get(r.schedMovieId);
  return versionLabel((movie?.versions || []).find((v) => v.versionId === r.versionId)) || `ID ${r.versionId}`;
};
const countLabel = (r) => {
  // 配分指定は回数でなく割合（対象の回のうち指定時間帯・スクリーンの回の割合）
  if (r.kind === "SHARE_PREF") return r.shareRatio == null ? "-" : `割合 ${r.shareRatio}%以上`;
  if (r.count == null) return "-";
  return `${r.count}${r.countUnit === "PER_DAY" ? "回/日" : "回"}`;
};
const timeLabel = (r) => (r.timeFrom || r.timeTo ? `${r.timeFrom || ""}〜${r.timeTo || ""}` : "-");
const screenLabel = (r) => (r.screenId == null ? "-" : screenMap.value.get(r.screenId)?.name || `ID ${r.screenId}`);

// 配給会社の要望で必須条件として扱われていない件数
const distributorSoftCount = computed(() => rules.value.filter(isDistributorSoftRule).length);
// 配給会社の要望で必須にならなかった行への注意文
const DISTRIBUTOR_SOFT_MSG = "配給会社の要望ですが、必須条件として扱われていません。内容を確認してください";

// 要確認行を目立たせる（配給会社の必須外を優先）
const rowClassName = ({ row }) => {
  if (isDistributorSoftRule(row)) return "is-distributor-soft";
  return row.resolve === "AMBIGUOUS" ? "is-ambiguous" : "";
};

// 「ルールを確定」を押せないか（解析中・保存中・生成中・前提不足のとき）
const confirmDisabled = computed(() => rulesParsing.value || rulesSaving.value || generating.value || !!generateBlockReason.value);
</script>

<template>
  <section v-loading="rulesParsing" element-loading-text="AIが要望をルールに解析しています…" class="panel">
    <div class="panel-head">
      <h3>要望ルール<span class="panel-count">{{ rules.length }}</span></h3>
      <div class="head-actions">
        <!-- 要望の修正はステップ1 で行う -->
        <el-button size="small" :disabled="rulesSaving" @click="emit('edit')">編集</el-button>
        <el-tooltip
          v-if="canEdit"
          :disabled="!generateBlockReason || rulesParsing"
          :content="generateBlockReason"
          placement="bottom"
        >
          <span>
            <el-button
              v-hasPermi="['dp:scheduleAi:plan:edit']"
              size="small"
              type="primary"
              :loading="rulesSaving"
              :disabled="confirmDisabled"
              @click="emit('confirm')"
            >
              ルールを確定
            </el-button>
          </span>
        </el-tooltip>
      </div>
    </div>

    <div class="rule-alerts">
      <!-- 解析の失敗（再試行はこのボタンからのみ） -->
      <el-alert v-if="parseError" type="error" :closable="false" show-icon :title="parseError">
        <el-button
          v-if="canEdit"
          v-hasPermi="['dp:scheduleAi:plan:edit']"
          size="small"
          type="danger"
          plain
          class="retry-btn"
          @click="state.parseRules()"
        >
          再試行
        </el-button>
      </el-alert>
      <template v-else-if="!rulesParsing">
        <el-alert
          v-if="isPreview"
          type="info"
          :closable="false"
          show-icon
          :title="canEdit
            ? 'AIが要望から解析したルールのプレビューです。内容を確認して「ルールを確定」すると、タイムチャートを作成します'
            : '要望が変更されていますが、ルールはまだ確定されていません'"
        />
        <el-alert
          v-if="ambiguousCount"
          type="warning"
          :closable="false"
          show-icon
          :title="`解釈できなかった要望が${ambiguousCount}件あります（黄色の行）。そのまま確定でき、原文を参考にAIが判断しますが、必ず反映されるとは限りません。修正する場合は「編集」で要望を見直してください`"
        />
        <el-alert
          v-if="distributorSoftCount"
          type="error"
          :closable="false"
          show-icon
          :title="`配給会社の要望のうち${distributorSoftCount}件が必須条件として扱われていません（赤い行）。回数が満たされる保証はないため、内容を確認してください`"
        />
      </template>
    </div>

    <el-empty
      v-if="!rules.length && !rulesParsing"
      :image-size="64"
      :description="hasRequestText ? 'ルールに変換できる要望はありませんでした' : '作品ごとの設定で配給要望・劇場要望を入力すると、ここにルールが表示されます'"
    />

    <el-table v-else-if="rules.length" :data="rules" row-key="_key" :row-class-name="rowClassName" class="rule-table">
      <el-table-column label="判定" width="92">
        <template #default="{ row }">
          <el-tag v-if="row.resolve === 'AMBIGUOUS'" type="warning" effect="dark" size="small">判定不可</el-tag>
          <el-tag v-else type="success" effect="plain" size="small">解析済</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="由来" width="96">
        <template #default="{ row }">
          {{ optionLabel(RULE_SOURCE_OPTIONS, row.source) }}
          <el-tag v-if="row.hard" type="danger" size="small" effect="plain" class="hard-tag">必須</el-tag>
          <el-tag v-else-if="isDistributorSoftRule(row)" type="danger" size="small" effect="dark" class="hard-tag">必須外</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="種類" width="110">
        <template #default="{ row }">{{ optionLabel(RULE_KIND_OPTIONS, row.kind) }}</template>
      </el-table-column>
      <el-table-column label="作品・バージョン" min-width="200">
        <template #default="{ row }">
          <div class="strong">{{ movieTitle(row.schedMovieId) }}</div>
          <div class="muted">{{ ruleVersionLabel(row) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="条件" min-width="200">
        <template #default="{ row }">
          <div>{{ periodLabel(row.dateFrom, row.dateTo) }}　{{ countLabel(row) }}</div>
          <div class="muted">時間帯 {{ timeLabel(row) }} / スクリーン {{ screenLabel(row) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="説明" min-width="220">
        <template #default="{ row }">
          <div>{{ row.description }}</div>
          <div v-if="row.excerpt" class="excerpt">原文：「{{ row.excerpt }}」</div>
          <!-- 配給会社の必須外：注意文と理由（hint は空のこともある） -->
          <template v-if="isDistributorSoftRule(row)">
            <div class="soft-warn">{{ DISTRIBUTOR_SOFT_MSG }}</div>
            <div v-if="row.hint" class="hint-text">理由：{{ row.hint }}</div>
          </template>
          <div v-else-if="row.resolve === 'AMBIGUOUS' && row.hint" class="hint-text">{{ row.hint }}</div>
        </template>
      </el-table-column>
    </el-table>
  </section>
</template>

<style lang="less" scoped>
.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.retry-btn {
  margin-top: 6px;
}

.rule-alerts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 18px 0;

  &:empty {
    display: none;
  }
}

.rule-table {
  margin-top: 8px;

  :deep(.is-ambiguous) {
    --el-table-tr-bg-color: color-mix(in srgb, var(--el-color-warning) 14%, var(--sp-panel));
  }

  :deep(.is-distributor-soft) {
    --el-table-tr-bg-color: color-mix(in srgb, var(--el-color-danger) 10%, var(--sp-panel));
  }
}

.soft-warn {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  color: var(--el-color-danger);
}

.hard-tag {
  margin-left: 4px;
}

.strong {
  font-weight: 600;
}

.excerpt {
  margin-top: 2px;
  font-size: 12px;
  color: var(--sp-muted);
}

.hint-text {
  width: 100%;
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--el-color-warning-dark-2, var(--el-color-warning));
}
</style>
